import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  ViewChild,
  signal,
  ChangeDetectorRef,
  inject,
  input,
} from '@angular/core';
import {
  DxButtonModule,
  DxDataGridModule,
  DxDateRangeBoxModule,
  DxFormComponent,
  DxFormModule,
  DxMultiViewModule,
  DxPopupModule,
  DxSelectBoxModule,
  DxTabsModule,
  DxTemplateModule,
  DxValidationGroupModule,
  DxValidatorModule,
  DxDateBoxModule,
} from 'devextreme-angular';
import { ContextService } from '../../services/context.service';
import { SharedModule } from '../../shared/shared.module';
import { ProjectDto } from './../../shared/generated/backend/model/projectDto';
import { FleetService, ProjectService, VehicleDto } from '../../shared/generated/backend';
import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';

export type FormStep = 'dateInput' | 'dateProcessing' | 'vehicleSelection' | 'creation' | 'cancelation' | 'discard';

@Component({
  selector: 'app-new-project-form',
  imports: [
    SharedModule,
    DxDataGridModule,
    DxTemplateModule,
    DxDateRangeBoxModule,
    DxSelectBoxModule,
    DxButtonModule,
    DxTabsModule,
    DxMultiViewModule,
    DxPopupModule,
    DxFormModule,
    DxValidationGroupModule,
    DxValidatorModule,
    DxDateBoxModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './new-project-form.component.html',
  styleUrl: './new-project-form.component.scss',
})
export class NewProjectFormComponent implements OnInit {
  readonly onShow = input<EventEmitter<void>>();

  @ViewChild(DxFormComponent, { static: false }) form!: DxFormComponent;
  steps: FormStep[] = ['dateInput', 'dateProcessing', 'vehicleSelection', 'creation', 'cancelation', 'discard'];
  currentStepIndex: number = 0;
  readonly currentStep = signal<FormStep>('dateInput');
  VehicleList: VehicleDto[] = [];
  project!: ProjectDto;
  errorMessage: string = '';
  showBackButton: boolean = false;

  actionButtonConfig: { text: 'BUTTONS.NEXT' | 'BUTTONS.CREATE' | 'BUTTONS.DISCARD'; visible: boolean } = {
    text: 'BUTTONS.NEXT',
    visible: false,
  };

  private contextService = inject(ContextService);
  private fleetService = inject(FleetService);
  private projectService = inject(ProjectService);
  private translationService = inject(TranslateService);
  private cdRef = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.defaultFromTime?.setHours(0, 0, 0, 0); // Set to 12:00
    this.defaultUntilTime?.setHours(0, 0, 0, 0);
    this.onShow()?.subscribe(() => {
      setTimeout(() => {
        this.resetForm();
      });
    });
  }

  //fromTime: string | null = '00:00';
  //untilTime: string | null = '00:00';
  defaultFromTime: Date | null = new Date();
  defaultUntilTime: Date | null = new Date();

  fromDate: Date | null = null;
  fromTime: Date | null = null;
  untilDate: Date | null = null;
  untilTime: Date | null = null;
  vehicleId: number = 0;

  updateFromDateTime(): void {
    // console.log('updateFromDateTime');
    if (this.fromDate && this.fromTime) {
      this.project.FromDateTime = this.combineDateAndTime(this.fromDate, this.fromTime);
    } else {
      this.project.FromDateTime = '';
    }
    this.validateStep();
  }

  updateUntilDateTime(): void {
    // console.log('updateUntilDateTime');
    if (this.untilDate && this.untilTime) {
      this.project.UntilDateTime = this.combineDateAndTime(this.untilDate, this.untilTime);
    } else {
      this.project.UntilDateTime = ''; // Prevents incorrect formatting
    }
    this.validateStep(); // Trigger validation after setting values
  }

  combineDateAndTime(date: Date, time: Date): string {
    if (!date || !time) return '';

    const formattedDate = format(date, 'yyyy-MM-dd');
    const formattedTime = format(time, 'HH:mm:ss');

    return `${formattedDate} ${formattedTime}`;
  }

  resetForm(): void {
    // console.log('resetForm');
    this.project = {} as ProjectDto;

    this.fromDate = null;
    this.fromTime = null;
    this.untilDate = null;
    this.untilTime = null;

    this.project.FromDateTime = '';
    this.project.UntilDateTime = '';

    this.form.instance?.reset();
    this.currentStepIndex = 0;
    this.actionButtonConfig.visible = false;
    this.showBackButton = false;
    this.currentStep.set(this.steps[this.currentStepIndex]);

    this.cdRef.detectChanges();
  }
  nextStep(): void {
    if (this.currentStepIndex < this.steps.length - 1) {
      this.currentStepIndex++;
      this.currentStep.set(this.steps[this.currentStepIndex]);
      this.showBackButton = true;
    } else {
      this.showBackButton = false;
    }
    this.validateStep();
    this.cdRef.markForCheck();
  }

  previousStep(): void {
    this.currentStepIndex = 0;
    this.currentStep.set(this.steps[this.currentStepIndex]);
    this.showBackButton = false;
    this.actionButtonConfig.text = 'BUTTONS.NEXT';
    this.validateStep();
    this.cdRef.detectChanges();
  }

  validateStep = () => {
    this.form.instance?.validate();
    if (this.currentStep() === 'dateInput') {
      const hasDates = !!this.project.FromDateTime && !!this.project.UntilDateTime;
      const isRangeValid = this.validateDateRange();

      this.actionButtonConfig.visible = hasDates && isRangeValid;
    }

    // specific action based on current step
    // console.log('currentStep', this.currentStep());
    switch (this.currentStep()) {
      case 'dateProcessing': // load available vehicles
        // console.log('vehicleSelection');
        this.showBackButton = false;
        this.actionButtonConfig.visible = false;
        this.fleetService
          .getAvailableVehicles(
            format(this.project.FromDateTime, "yyyy-MM-dd'T'HH:mm:ss"),
            format(this.project.UntilDateTime, "yyyy-MM-dd'T'HH:mm:ss")
          )
          .subscribe({
            next: returnValue => {
              this.VehicleList = returnValue;
              this.nextStep(); // move on to vehicleSelection
            },
            error: err => {
              this.errorMessage = err.statusText;
            },
          });
        break;
      case 'vehicleSelection':
        this.showBackButton = true;
        this.actionButtonConfig.text = 'BUTTONS.CREATE';
        this.actionButtonConfig.visible = !!this.project.VehicleId;
        this.actionButtonConfig.visible = this.vehicleId != null;
        this.actionButtonConfig.visible = this.project.VehicleId != null; // show create button if vehicle selected
        this.cdRef.markForCheck();
        break;
      case 'creation':
        this.showBackButton = false;
        this.actionButtonConfig.visible = false;
        // console.log('creation', this.project);
        this.createProject();
        break;
      case 'cancelation':
        this.actionButtonConfig.text = 'BUTTONS.DISCARD';
        this.actionButtonConfig.visible = true;
        this.showBackButton = false;
        this.resetForm();
        break;
      case 'discard':
        // console.log('cancelation');
        this.resetForm();
        break;
    }
  };

  async createProject() {
    this.projectService
      .createProject({
        fromDate: this.project.FromDateTime,
        toDate: this.project.UntilDateTime,
        vehicleId: this.project.VehicleId!,
      })
      .subscribe({
        next: returnValue => {
          this.project.Id = returnValue.id;
          this.contextService.openContextView('project/' + this.project.Id);
        },
        error: err => {
          console.error(err);
          this.errorMessage = this.translationService.instant('ERROR_STATUS.' + err.statusText);
          this.nextStep(); // go to cancelation
        },
      });
  }

  validateDateRange = (): boolean => {
    if (!this.project.FromDateTime || !this.project.UntilDateTime) {
      return true;
    }
    return this.project.FromDateTime <= this.project.UntilDateTime;
  };
}
